// Push notification helper - uses Commhub's Firebase config via /api/push/config
import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { api } from "@/lib/api";

let _config = null;
let _messaging = null;

// SW-URL versionsstämplas med körande bundle-hash + SW_REV så varken
// webbläsarens HTTP-cache eller CDN:ets edge-cache (max-age 4h) kan servera
// en gammal SW. SW_REV MÅSTE bumpas vid varje ändring i firebase-messaging-sw.js
// (SW-filen ändrar inte bundle-hashen eftersom den ligger i public/).
const SW_REV = "208";
export function swUrl() {
  try {
    const script = [...document.querySelectorAll("script[src]")]
      .map((s) => s.getAttribute("src"))
      .find((src) => /\/static\/js\/main\.[0-9a-f]+\.js/.test(src || ""));
    const m = script && script.match(/main\.([0-9a-f]+)\.js/);
    if (m) return `/firebase-messaging-sw.js?v=${m[1]}-r${SW_REV}`;
  } catch { /* tyst */ }
  return "/firebase-messaging-sw.js";
}

export function registerSW() {
  return navigator.serviceWorker.register(swUrl(), { updateViaCache: "none" });
}

async function loadConfig() {
  if (_config) return _config;
  const { data } = await api.get("/push/config");
  _config = data;
  return _config;
}

async function initMessaging() {
  if (_messaging) return _messaging;
  const supported = await isSupported().catch(() => false);
  if (!supported) throw new Error("Push stöds inte i denna webbläsare");
  const cfg = await loadConfig();
  if (!cfg?.configured || !cfg?.config) throw new Error("Commhub Firebase-config saknas");
  const app = getApps().length ? getApps()[0] : initializeApp(cfg.config);
  _messaging = getMessaging(app);
  return _messaging;
}

export async function pushSupported() {
  try {
    if (!("serviceWorker" in navigator)) return false;
    if (!("Notification" in window)) return false;
    const supported = await isSupported().catch(() => false);
    return supported;
  } catch {
    return false;
  }
}

export async function getPushStatus() {
  const supported = await pushSupported();
  const cfg = await loadConfig().catch(() => null);
  return {
    supported,
    permission: typeof Notification !== "undefined" ? Notification.permission : "denied",
    vapid_configured: !!cfg?.vapidKey,
    firebase_configured: !!cfg?.configured,
  };
}

export async function enablePush() {
  const status = await getPushStatus();
  if (!status.supported) throw new Error("Push stöds inte");
  if (!status.firebase_configured) throw new Error("Firebase ej konfigurerat i Commhub");
  if (!status.vapid_configured) throw new Error("VAPID-nyckel saknas. Lägg in den i Commhub → Push-inställningar");

  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Tillstånd nekat");

  // Register FCM service worker
  const swReg = await registerSW();

  const messaging = await initMessaging();
  const cfg = await loadConfig();
  const token = await getToken(messaging, {
    vapidKey: cfg.vapidKey,
    serviceWorkerRegistration: swReg,
  });
  if (!token) throw new Error("Kunde inte få token");

  await api.post("/push/register", { device_token: token, platform: "web" });
  localStorage.setItem("grindstugatan_push_token", token);

  // Foreground messages → show toast / browser notification
  // Vid data-only payload: använd `data.sender` som rubrik så det står
  // "Edit din assistent" istället för bara appnamnet.
  // OBS: `from` är RESERVERAD i FCM data-payload — använd `sender`.
  onMessage(messaging, (payload) => {
    if (payload.notification) {
      // FCM har redan visat notisen — gör inget mer, undvik dubblett.
      return;
    }
    const fromName = payload.data?.sender || payload.data?.from || "Edit din assistent";
    const innerTitle = payload.data?.title || "";
    const body = payload.data?.body || "";
    const combinedBody = innerTitle ? `${innerTitle}\n${body}`.trim() : body;
    try {
      new Notification(fromName, { body: combinedBody, icon: "/icon-192.png" });
    } catch (_) {}
  });

  return token;
}

/**
 * Hämta enhetens aktuella FCM-token — självläkande.
 * localStorage kan rensas av iOS/Safari medan FCM-registreringen (och därmed
 * push-leveransen) lever kvar. Om permission är granted men localStorage är
 * tom hämtar vi samma token tyst från FCM igen och sparar den på nytt.
 */
export async function getCurrentToken() {
  try {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return null;
    const cached = localStorage.getItem("grindstugatan_push_token");
    if (cached) return cached;
    const supported = await pushSupported();
    if (!supported) return null;
    const swReg = await registerSW();
    const messaging = await initMessaging();
    const cfg = await loadConfig();
    const token = await getToken(messaging, {
      vapidKey: cfg.vapidKey,
      serviceWorkerRegistration: swReg,
    });
    if (token) localStorage.setItem("grindstugatan_push_token", token);
    return token || null;
  } catch {
    return null;
  }
}

export async function disablePush() {
  const token = await getCurrentToken();
  if (token) {
    await api.delete("/push/token", { data: { device_token: token, platform: "web" } });
    localStorage.removeItem("grindstugatan_push_token");
  }
}

export async function testPush() {
  const { data } = await api.post("/push/test");
  return data;
}

/**
 * Heartbeat — uppdaterar push_tokens.last_seen_at för denna enhet.
 * Anropas på app-focus + var 5:e min så "Aktiv · senast sedd" speglar
 * verklig app-användning, inte bara registreringstillfället.
 * Tyst (no toast) — körs i bakgrunden.
 */
export async function pushHeartbeat() {
  const token = await getCurrentToken();
  if (!token) return false;
  try {
    await api.post("/push/heartbeat", { device_token: token });
    return true;
  } catch {
    return false;
  }
}

/**
 * Verifierar att den lokala FCM-token:en faktiskt är aktivt registrerad
 * i backend. Returnerar { registered, enabled, reason? } eller null om
 * ingen token finns lokalt.
 */
export async function pushVerify() {
  const token = await getCurrentToken();
  if (!token) return null;
  try {
    const { data } = await api.post("/push/verify", { device_token: token });
    return data;
  } catch {
    return { registered: false, enabled: false, reason: "verify_failed" };
  }
}

// -----------------------------------------------------------------------------
// Diagnostik — steg-för-steg felsökning. Varje steg får egen status + felmeddelande.
// Anropas av "Diagnostisera push"-knappen i Settings.
// -----------------------------------------------------------------------------
export async function pushDiagnostics() {
  const steps = [];
  const push = (name, status, detail = "") => steps.push({ name, status, detail });

  // 1. Browser stöd
  try {
    const hasSW = "serviceWorker" in navigator;
    const hasNoti = "Notification" in window;
    const fcmOk = await isSupported().catch(() => false);
    if (!hasSW) push("Service Worker API", "fail", "Saknas i denna webbläsare");
    else if (!hasNoti) push("Notification API", "fail", "Saknas i denna webbläsare");
    else if (!fcmOk) push("Firebase Messaging stöd", "fail", "Webbläsaren stöder inte FCM (vanligt på iOS utan PWA-installation)");
    else push("Browser-stöd", "ok", "SW + Notification + FCM stöds");
  } catch (e) {
    push("Browser-stöd", "fail", String(e?.message || e));
  }

  // 2. Permission-state
  try {
    const hasNotification = typeof Notification !== "undefined";
    if (!hasNotification) {
      push("Notification.permission", "fail",
        "Notification API finns inte — iOS Safari kräver att appen INSTALLERAS som PWA via 'Lägg till på hemskärmen' först");
    } else {
      const perm = Notification.permission;
      push("Notification.permission",
        perm === "granted" ? "ok" : perm === "denied" ? "fail" : "warn",
        perm === "granted"
          ? "Tillåten"
          : perm === "denied"
          ? "BLOCKERAT av webbläsaren. Måste återställas manuellt via 🔒-ikonen → Notiser → Återställ"
          : "Inte tillfrågad än — popup kommer visas när du klickar Aktivera");
    }
  } catch (e) {
    push("Notification.permission", "fail", String(e?.message || e));
  }

  // 3. Backend-config (Commhub via vår proxy)
  try {
    const cfg = await loadConfig();
    if (!cfg?.configured) push("Backend /push/config", "fail", "configured=false från Commhub");
    else if (!cfg?.config?.messagingSenderId) push("Firebase-config", "fail", "messagingSenderId saknas");
    else if (!cfg?.vapidKey) push("VAPID-nyckel", "fail", "Saknas i Commhub Dashboard → Push-inställningar");
    else push("Backend-config", "ok", `VAPID ${cfg.vapidKey.slice(0, 16)}…, projectId ${cfg.config.projectId}`);
  } catch (e) {
    push("Backend /push/config", "fail", String(e?.message || e));
  }

  // 4. Service Worker registrering
  let swReg = null;
  try {
    swReg = await Promise.race([
      registerSW(),
      new Promise((_, rej) => setTimeout(() => rej(new Error("SW-register timeout (>10s)")), 10000)),
    ]);
    push("Service Worker register", "ok", `scope=${swReg.scope}`);
  } catch (e) {
    push("Service Worker register", "fail", String(e?.message || e));
  }

  // 5. SW ready
  if (swReg) {
    try {
      await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, rej) => setTimeout(() => rej(new Error("SW.ready timeout (>10s)")), 10000)),
      ]);
      push("Service Worker ready", "ok", "active");
    } catch (e) {
      push("Service Worker ready", "fail", String(e?.message || e));
    }
  }

  // 6. Firebase init
  try {
    await initMessaging();
    push("Firebase init", "ok", "Messaging initialiserat");
  } catch (e) {
    push("Firebase init", "fail", String(e?.message || e));
  }

  // 7. Token-skapande (om permission redan är granted) — med timeout
  if (typeof Notification !== "undefined" && Notification.permission === "granted" && swReg) {
    try {
      const cfg = await loadConfig();
      const messaging = await initMessaging();
      const token = await Promise.race([
        getToken(messaging, { vapidKey: cfg.vapidKey, serviceWorkerRegistration: swReg }),
        new Promise((_, rej) => setTimeout(() => rej(new Error("getToken timeout (>15s)")), 15000)),
      ]);
      if (!token) push("FCM token", "fail", "getToken returnerade tomt — FCM kunde inte registrera");
      else push("FCM token", "ok", `${token.slice(0, 30)}… (${token.length} tecken)`);
    } catch (e) {
      push("FCM token", "fail", String(e?.message || e));
    }
  } else if (typeof Notification !== "undefined" && Notification.permission !== "granted") {
    push("FCM token", "skip", "Hoppar över — permission ej granted ännu (klicka Aktivera först)");
  }

  return steps;
}
